import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import Button from '@material-ui/core/Button';
import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import GetAppIcon from '@material-ui/icons/GetApp';
import { compact, get, includes, isEmpty, map, orderBy as _orderBy, take } from 'lodash';
import { Patient, usePatientByIds } from 'requests/resources/patient';
import { getFirstActiveContact } from 'ui-utils';
import { useDownloadExcelFile } from 'ui-model';

import { Patient as TPatientLegacy, UserBase } from '@lib/resources/types';

import { Contact } from '../Contact';
import EnhancedTable from '../EnhancedTable';
import PatientActions from '../PatientActions';

type ColumnId =
  | 'name'
  | 'contact'
  | 'dateOfBirth'
  | 'externalId.value'
  | 'appointment.date'
  | 'appointment.facility'
  | 'appointment.provider'
  | 'appointment.type'
  | 'appointment.status'
  | 'stats.counters.confirmedAppointments'
  | 'stats.counters.cancelledAppointments'
  | 'stats.counters.messagesSentToPatient'
  | 'stats.counters.offersSent'
  | 'actions';

export type PatientsTableColumn = {
  id: ColumnId;
  label?: string;
  padding?: 'checkbox';
  callback?: (item: any) => any;
  sort?: (items: any[], order: 'asc' | 'desc') => any[];
  date?: string | boolean;
  numeric?: boolean;
  style?: any;
};

export const callbackContact = (item: UserBase) => <Contact contact={getFirstActiveContact(item)} />;

export const sortContact = (items: Patient[], order: 'asc' | 'desc') =>
  _orderBy(items, [(item) => get(getFirstActiveContact(item), 'value')], [order]);

const columnDataDefault: PatientsTableColumn[] = [
  { id: 'name', label: 'Name', padding: 'checkbox', style: { width: 180 } },
  {
    id: 'contact',
    label: 'Contact',
    padding: 'checkbox',
    callback: callbackContact,
    sort: sortContact,
    style: { width: 220 },
  },
  { id: 'dateOfBirth', label: 'DOB', padding: 'checkbox', date: 'll', style: { width: 100 } },
  { id: 'appointment.date', label: 'Next/last appt', padding: 'checkbox', date: 'lll', style: { width: 130 } },
  {
    id: 'stats.counters.confirmedAppointments',
    padding: 'checkbox',
    label: 'Appts',
    numeric: true,
    style: { width: 100 },
  },
  {
    id: 'stats.counters.cancelledAppointments',
    padding: 'checkbox',
    label: 'CX Appts',
    numeric: true,
    style: { width: 100 },
  },
  {
    id: 'stats.counters.messagesSentToPatient',
    padding: 'checkbox',
    label: 'Messages',
    numeric: true,
    style: { width: 100 },
  },
  { id: 'stats.counters.offersSent', padding: 'checkbox', label: 'Offers', numeric: true, style: { width: 100 } },
  { id: 'actions', padding: 'checkbox' },
];

const broadcastColumnData: PatientsTableColumn[] = [
  { id: 'name', label: 'Name', padding: 'checkbox', style: { width: 180 } },
  {
    id: 'contact',
    label: 'Contact',
    padding: 'checkbox',
    callback: callbackContact,
    sort: sortContact,
    style: { width: 220 },
  },
  { id: 'dateOfBirth', label: 'DOB', padding: 'checkbox', date: 'll', style: { width: 130 } },
  { id: 'appointment.date', label: 'Appt Date', padding: 'checkbox', date: 'lll', style: { width: 130 } },
  { id: 'appointment.facility', label: 'Facility', padding: 'checkbox', style: { width: 150 } },
  { id: 'appointment.provider', label: 'Provider', padding: 'checkbox', style: { width: 150 } },
  { id: 'appointment.type', label: 'Appt Type', padding: 'checkbox', style: { width: 150 } },
  { id: 'appointment.status', label: 'Appt Status', padding: 'checkbox', style: { width: 150 } },
];

const styles = ({
  palette: {
    common: { white },
    primary,
  },
  spacing: { unit },
}: Theme) =>
  createStyles({
    buttonDownload: {
      backgroundColor: white,
      boxShadow: 'none',
      lineHeight: `${unit * 3}px`,
      marginLeft: unit * 2,
      minWidth: 48,
      textAlign: 'center',
    },
    buttonDownloadIcon: {
      color: primary.main,
      fontSize: 18,
    },
    root: {
      background: white,
      flex: 1,
      overflow: 'auto',
      paddingTop: 15,
    },
    title: {
      display: 'inline-block',
      fontWeight: 'bold',
      marginTop: unit,
      marginBottom: unit * 3,
      marginLeft: unit * 1.5,
    },
  });

const messages = defineMessages({
  title: {
    id: 'components.PatientsTable.title',
    defaultMessage: '{length, plural, one {# patient} other {# patients}}',
  },
  titleFirstResults: {
    id: 'components.PatientsTable.titleFirstResults',
    defaultMessage:
      '{length, plural, one {# patient. Here are the first {maxRows} results:} other {# patients. Here are the first {maxRows} results:}}',
  },
});

// TODO cleanup props by extending `EnhancedTableProps` once it's ready
export interface PatientsTableProps extends WithStyles<typeof styles> {
  columnData?: PatientsTableColumn[];
  downloadPatientsLink?: string;
  hideActions?: boolean;
  hideMerge?: boolean;
  includeTitle?: boolean;
  page?: string;
  patients: (string | Partial<Patient> | Partial<TPatientLegacy>)[];
  maxRows?: number;
  onCheck?: (...args: any[]) => any;
  onItemSelected?: (...args: any[]) => any;
  onPatientMerge?: Function;
  order?: string;
  orderBy?: string;
  patientsSelected?: (string | boolean)[];
  patientsRejected?: (string | boolean)[];
  selectable?: boolean;
  columnsToHide?: any[];
  isSelectAllMarked?: boolean;
  loading?: boolean;
  disableSort?: boolean;
}

const PatientsTable: React.FC<PatientsTableProps> = ({
  classes,
  columnData,
  columnsToHide,
  onCheck,
  onItemSelected,
  order,
  orderBy,
  patients,
  includeTitle,
  patientsSelected,
  patientsRejected,
  downloadPatientsLink,
  selectable,
  maxRows,
  isSelectAllMarked,
  page,
  loading,
  disableSort,
  hideActions,
  hideMerge,
  onPatientMerge,
}) => {
  const [isDownloadExcelTriggered, setDownloadExcelTrigger] = useState(false);
  const dataReceived = maxRows ? take(patients, maxRows) : patients;

  // Fetch patients in case of receiving IDs
  // We're doing the fetch inside this component since we don't want to fetch the whole list but only first `maxRows`
  const ids = compact(map(dataReceived, (data) => get(data, 'id')));
  const isFetchEnabled = !isEmpty(ids);
  const { data: dataFetched } = usePatientByIds({ ids }, { enabled: isFetchEnabled });

  useDownloadExcelFile({
    url: downloadPatientsLink || '',
    fileName: 'patients.xlsx',
    isEnabled: isDownloadExcelTriggered,
    onSettled: () => setDownloadExcelTrigger(false),
  });

  return (
    <div className={classes.root}>
      {includeTitle && (
        <>
          <Typography className={classes.title} component="span" gutterBottom>
            <FormattedMessage
              {...(maxRows && maxRows < patients.length ? messages.titleFirstResults : messages.title)}
              values={{ length: patients.length, maxRows }}
            />
          </Typography>
          {downloadPatientsLink && (
            <Button
              data-lh-id="PatientsTable-btn-download"
              className={classes.buttonDownload}
              onClick={() => setDownloadExcelTrigger(true)}
              variant="outlined"
              rel="noopener"
            >
              <GetAppIcon className={classes.buttonDownloadIcon} />
            </Button>
          )}
        </>
      )}
      <EnhancedTable
        data={isFetchEnabled ? dataFetched || [] : dataReceived}
        itemsSelected={patientsSelected}
        itemsRejected={patientsRejected}
        isSelectAllMarked={isSelectAllMarked}
        // @ts-ignore
        columnData={
          page === 'broadcast'
            ? broadcastColumnData
            : columnData || columnDataDefault.filter(({ id }) => !includes(columnsToHide, id))
        }
        selectable={selectable}
        onClick={onItemSelected}
        // @ts-ignore
        onCheck={onCheck}
        // @ts-ignore
        order={order}
        orderBy={orderBy}
        loading={loading}
        disableSort={disableSort}
        renderActions={(patient: Patient) => {
          if (hideActions) return;
          return <PatientActions hideMerge={hideMerge} item={patient} onPatientMerge={onPatientMerge} />;
        }}
      />
    </div>
  );
};

export default withStyles(styles)(PatientsTable);
